import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { Alert, Button, message, Modal, Spin, Tooltip } from "antd";
import PropTypes from "prop-types";
import React, { Component } from "react";

const SQL_KEYWORDS = [
    "CREATE",
    "TABLE",
    "ALTER",
    "DROP",
    "INSERT",
    "INTO",
    "VALUES",
    "SELECT",
    "FROM",
    "WHERE",
    "NOT",
    "NULL",
    "PRIMARY",
    "KEY",
    "FOREIGN",
    "REFERENCES",
    "DEFAULT",
    "UNIQUE",
    "INDEX",
    "CONSTRAINT",
    "CHECK",
    "IF",
    "EXISTS",
    "CASCADE",
    "AUTO_INCREMENT",
    "IDENTITY",
    "SERIAL",
    "ON",
    "DELETE",
    "UPDATE",
    "SET",
    "ADD",
    "COLUMN",
];

const SQL_TYPES = [
    "VARCHAR",
    "CHAR",
    "TEXT",
    "INT",
    "INTEGER",
    "BIGINT",
    "SMALLINT",
    "TINYINT",
    "FLOAT",
    "DOUBLE",
    "DECIMAL",
    "NUMERIC",
    "BOOLEAN",
    "BOOL",
    "DATE",
    "DATETIME",
    "TIMESTAMP",
    "TIME",
    "BLOB",
    "CLOB",
    "UUID",
    "JSON",
    "JSONB",
    "XML",
    "BINARY",
    "VARBINARY",
    "NVARCHAR",
    "NCHAR",
    "REAL",
    "BIT",
];

function highlightSql(ddl) {
    const lines = ddl.split("\n");
    return lines.map((line, lineIdx) => {
        const commentIndex = line.indexOf("--");
        let codePart = commentIndex >= 0 ? line.substring(0, commentIndex) : line;
        let commentPart = commentIndex >= 0 ? line.substring(commentIndex) : "";

        const codeElements = [];
        if (codePart) {
            const tokens = codePart.split(/(\b|\s+|[(),;])/);
            tokens.forEach((token, i) => {
                const upperToken = token.toUpperCase();
                if (SQL_KEYWORDS.includes(upperToken)) {
                    codeElements.push(
                        <span key={`k${i}`} style={{ color: "#569cd6", fontWeight: 600 }}>
                            {token}
                        </span>,
                    );
                } else if (SQL_TYPES.some((t) => upperToken === t || upperToken.match(new RegExp(`^${t}\\b`)))) {
                    codeElements.push(
                        <span key={`t${i}`} style={{ color: "#4ec9b0" }}>
                            {token}
                        </span>,
                    );
                } else if (token.match(/^'.*'$/)) {
                    codeElements.push(
                        <span key={`s${i}`} style={{ color: "#ce9178" }}>
                            {token}
                        </span>,
                    );
                } else if (token.match(/^\d+$/)) {
                    codeElements.push(
                        <span key={`n${i}`} style={{ color: "#b5cea8" }}>
                            {token}
                        </span>,
                    );
                } else {
                    codeElements.push(<React.Fragment key={`x${i}`}>{token}</React.Fragment>);
                }
            });
        }

        const commentElement = commentPart ? <span style={{ color: "#6a9955", fontStyle: "italic" }}>{commentPart}</span> : null;

        return (
            <React.Fragment key={lineIdx}>
                {codeElements}
                {commentElement}
                {lineIdx < lines.length - 1 ? "\n" : ""}
            </React.Fragment>
        );
    });
}

class DDLSqlExportModal extends Component {
    handleCopy = () => {
        const { ddlSqlData } = this.props;
        if (ddlSqlData && ddlSqlData.ddl) {
            navigator.clipboard
                .writeText(ddlSqlData.ddl)
                .then(() => {
                    message.success("DDL SQL copied to clipboard");
                })
                .catch(() => {
                    message.error("Failed to copy to clipboard");
                });
        }
    };

    handleDownload = () => {
        const { ddlSqlData } = this.props;
        if (!ddlSqlData?.ddl) return;

        const fileNameBase = ddlSqlData.schemaName || ddlSqlData.schemaId || "ddl-export";
        const safeFileName = fileNameBase.replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
        const fileName = `${safeFileName || "ddl-export"}.sql`;

        const blob = new Blob([ddlSqlData.ddl], { type: "text/sql;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    render() {
        const { visible, onClose, generateDdlSqlResult, ddlSqlData } = this.props;
        const isLoading = generateDdlSqlResult?.loading;
        const hasError = generateDdlSqlResult && !generateDdlSqlResult.success && !generateDdlSqlResult.loading;
        const errorMessage = generateDdlSqlResult?.message;

        return (
            <Modal
                title="✨ SQL Export (DDL)"
                open={visible}
                onCancel={onClose}
                width={720}
                destroyOnClose
                footer={[
                    ddlSqlData?.ddl && (
                        <React.Fragment key="ddl-actions">
                            <Tooltip title="Copy DDL to clipboard" key="copy">
                                <Button icon={<CopyOutlined />} onClick={this.handleCopy}>
                                    Copy
                                </Button>
                            </Tooltip>
                            <Tooltip title="Download DDL as SQL file" key="download">
                                <Button icon={<DownloadOutlined />} onClick={this.handleDownload}>
                                    Download SQL
                                </Button>
                            </Tooltip>
                        </React.Fragment>
                    ),
                    <Button key="done" type="primary" onClick={onClose}>
                        Done
                    </Button>,
                ]}
            >
                {isLoading && (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16, color: "#888" }}>Generating DDL SQL...</p>
                    </div>
                )}

                {hasError && (
                    <Alert
                        type="error"
                        showIcon
                        message="Failed to generate DDL SQL"
                        description={errorMessage || "An unexpected error occurred. Please try again."}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!isLoading && ddlSqlData?.ddl && (
                    <div
                        style={{
                            background: "#1e1e1e",
                            borderRadius: 8,
                            padding: 20,
                            maxHeight: 500,
                            overflowY: "auto",
                            overflowX: "auto",
                        }}
                    >
                        <pre
                            style={{
                                margin: 0,
                                color: "#d4d4d4",
                                fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', 'Courier New', monospace",
                                fontSize: 13,
                                lineHeight: 1.6,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            <code>{highlightSql(ddlSqlData.ddl)}</code>
                        </pre>
                    </div>
                )}

                {!isLoading && !hasError && !ddlSqlData?.ddl && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>No DDL data available.</div>}
            </Modal>
        );
    }
}

DDLSqlExportModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    generateDdlSqlResult: PropTypes.shape({
        success: PropTypes.bool,
        loading: PropTypes.bool,
        message: PropTypes.string,
    }),
    ddlSqlData: PropTypes.shape({
        schemaId: PropTypes.string,
        businessAreaId: PropTypes.string,
        schemaName: PropTypes.string,
        ddl: PropTypes.string,
    }),
};

export default DDLSqlExportModal;
